import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Copy, CheckCircle, Users, TrendingUp, RefreshCw, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralProgram() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [signupOffer, setSignupOffer] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, statsRes, offerRes] = await Promise.allSettled([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/client/referrals/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/active-client-signup-offer`).catch(() => ({ data: {} })),
      ]);

      const me = meRes.status === 'fulfilled' ? meRes.value.data : {};
      setClientInfo(me);

      // SP/CP clients cannot access this page
      if (me?.created_by_admin_direct === false) {
        setAccessDenied(true);
        return;
      }

      if (statsRes.status === 'fulfilled' && statsRes.value.data) {
        setReferralStats(statsRes.value.data);
      }
      if (offerRes.status === 'fulfilled' && offerRes.value.data?.id) {
        setSignupOffer(offerRes.value.data);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyReferralLink = () => {
    const link = `${window.location.origin}/client/signup?ref=${clientInfo.account_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  // Dynamic amounts from active signup offer, or fallback to 500
  const refereeAmount = signupOffer?.referee_credit_amount ?? 500;
  const referrerAmount = signupOffer?.referrer_credit_amount ?? 500;

  if (accessDenied) {
    return (
      <DashboardLayout role="client">
        <div className="p-6 max-w-md mx-auto mt-16 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Not Available</h2>
          <p className="text-sm text-slate-500 mb-4">The Referral Program is available only for Direct Clients.</p>
          <Button variant="outline" onClick={() => navigate('/client/dashboard')}>Go to Dashboard</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="client">
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Gift className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-800">Referral Program</h1>
              <p className="text-sm text-slate-500">
                Invite clients — you earn ₹{referrerAmount.toLocaleString('en-IN')} when they activate their first service
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="referral-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-6 w-6 animate-spin text-emerald-500 mr-2" />
            <span className="text-slate-500">Loading referral data…</span>
          </div>
        ) : (
          <>
            {/* Referral link card */}
            <Card className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-sm mb-5" data-testid="referral-program-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-emerald-700 mb-1">
                      <strong>You earn:</strong> ₹{referrerAmount.toLocaleString('en-IN')} when your referral activates their first service
                    </p>
                    <p className="text-sm text-emerald-700 mb-3">
                      <strong>They earn:</strong> ₹{refereeAmount.toLocaleString('en-IN')} instant wallet credit on signup
                    </p>

                    {/* Referral code + copy button */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0 bg-white border border-emerald-200 rounded-lg px-3 py-2">
                        <p className="text-xs text-slate-500 mb-0.5">Your referral code</p>
                        <p
                          className="font-mono font-semibold text-slate-800 text-sm tracking-wider"
                          data-testid="referral-code"
                        >
                          {clientInfo?.account_id ?? '—'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:border-emerald-600 whitespace-nowrap"
                        onClick={copyReferralLink}
                        data-testid="copy-referral-link-btn"
                      >
                        <Copy className="h-4 w-4 mr-1.5" />Copy Link
                      </Button>
                    </div>
                  </div>

                  {/* Bonus badge */}
                  <div className="hidden md:flex flex-col items-center justify-center bg-emerald-100 rounded-xl px-5 py-4 text-center shrink-0">
                    <p className="text-2xl font-bold text-emerald-700">₹{referrerAmount.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-emerald-600 mt-0.5">per referral</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-5" data-testid="referral-stats">
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-4 text-center">
                  <Users className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-total-referrals">
                    {referralStats ? referralStats.total_referrals : '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Total Invited</p>
                </CardContent>
              </Card>
              <Card className="border border-emerald-200 shadow-sm">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="h-5 w-5 text-emerald-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-700" data-testid="stat-activated-referrals">
                    {referralStats ? referralStats.activated_referrals : '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Services Activated</p>
                </CardContent>
              </Card>
              <Card className="border border-emerald-200 shadow-sm">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-5 w-5 text-emerald-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-700" data-testid="stat-bonus-earned">
                    {referralStats ? `₹${referralStats.total_bonus_earned.toLocaleString('en-IN')}` : '—'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Bonus Earned</p>
                </CardContent>
              </Card>
            </div>

            {/* Referral list */}
            {referralStats && referralStats.referrals.length > 0 ? (
              <Card className="border border-slate-200 shadow-sm overflow-hidden" data-testid="referral-list-card">
                <div className="px-4 py-3 border-b border-slate-100 bg-emerald-50/60">
                  <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Referred Clients</p>
                </div>
                <ul className="divide-y divide-slate-50" data-testid="referral-list">
                  {referralStats.referrals.map(r => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                      data-testid={`referral-item-${r.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                        <p className="text-xs text-slate-400">
                          {r.email !== r.name ? r.email : ''}
                          {r.joined_at
                            ? ` · Joined ${new Date(r.joined_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : ''}
                        </p>
                      </div>
                      <span
                        className={`ml-3 shrink-0 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          r.bonus_paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}
                        data-testid={`referral-status-${r.id}`}
                      >
                        {r.bonus_paid ? (
                          <><CheckCircle className="h-3 w-3" />₹{referrerAmount.toLocaleString('en-IN')} Paid</>
                        ) : (
                          <>Awaiting activation</>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : referralStats ? (
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="px-4 py-8 text-center">
                  <Gift className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No referrals yet — share your link to get started!</p>
                </CardContent>
              </Card>
            ) : null}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
